/* ============================================================
 * ADG Lightbox — shared full-size artwork viewer.
 *
 * The Build-Your-Own builders have had a tap-to-enlarge viewer since
 * May; the browsing grids never did. Nathan asked for it site-wide
 * ("we're so visual"), so this is that same idea pulled out into one
 * component any grid can call.
 *
 *   ADGLightbox.open(items, index, opts)
 *
 *     items  [{ image, title, artist, href, ctaLabel }]  the whole
 *            visible list, so the viewer can page through it
 *     index  which one to show first
 *     opts   { onIndex } — called when the viewer moves, so the host
 *            grid can keep loading ahead of it
 *
 * WHY IT TAKES THE WHOLE LIST, not one item: opening a picture out of
 * a grid of 600 and being unable to go to the next one is a dead end.
 * Arrows, arrow keys and swipe all work, and the neighbouring images
 * are preloaded so paging is instant.
 *
 * The image shown is the artwork index's `img_full` (1024px). Justin's
 * caveat stands: product MOCKUPS are low-res, which is why this viewer
 * is pointed at flat artwork only and never scales past its natural
 * size (`max-height` in CSS is in vh, and the img is `object-fit:
 * contain` — it letterboxes rather than upscaling into mush).
 * ============================================================ */
(function (window, document) {
  'use strict';

  var HAS_DIALOG = typeof window.HTMLDialogElement === 'function' &&
    typeof document.createElement('dialog').showModal === 'function';

  var dlg = null, els = null;
  var items = [], idx = 0;
  var onIndex = null;
  /* opts.action — a host-supplied button in the caption, same slot as the
     CTA link. The design grids pass none (their CTA navigates); the BYO
     builders pass one so a shopper can ADD the design to their picks while
     swiping through, without leaving the viewer:
       { state: function (item, idx) -> { label, disabled },
         onAct: function (item, idx) }
     After every onAct the state() is asked again, so the label always
     tells the truth ("· 4 of 8 picked", "Your set is full"). */
  var action = null;
  var escHandler = null;
  var prevOverflow = '';
  var prevFocus = null;
  var preloaded = {};

  /* GIVING FOCUS BACK WITHOUT DRAWING A RING AROUND IT.
     Closing returns focus to whatever opened the lightbox, which is right for
     the keyboard and for screen readers. But WebKit matches :focus-visible on
     a PROGRAMMATIC .focus() where Chromium does not — so in Safari the card
     you had just clicked came back wearing a heavy black outline
     (Justin, 2026-08-16: "this stupid black box around it when it closes").
     Dropping the focus restore would break keyboard use, so instead: remember
     how the lightbox was opened, and only suppress the ring when it was opened
     with a pointer. The moment the user touches a key the ring is allowed back,
     so tabbing still shows where you are. */
  var lastInputKeyboard = false;
  document.addEventListener('keydown', function () { lastInputKeyboard = true; }, true);
  document.addEventListener('pointerdown', function () { lastInputKeyboard = false; }, true);
  document.addEventListener('mousedown', function () { lastInputKeyboard = false; }, true);
  var openedByKeyboard = false;

  function restoreFocus(el, viaKeyboard) {
    if (!el || typeof el.focus !== 'function') return;
    if (viaKeyboard) { el.focus(); return; }
    el.setAttribute('data-adg-noring', '');
    try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
    function clear() {
      el.removeAttribute('data-adg-noring');
      el.removeEventListener('blur', clear);
      document.removeEventListener('keydown', clear, true);
    }
    el.addEventListener('blur', clear);
    document.addEventListener('keydown', clear, true);
  }

  function build() {
    if (dlg) return dlg;
    dlg = document.createElement('dialog');
    dlg.className = 'adg-lb';
    dlg.setAttribute('aria-label', 'Artwork preview');
    dlg.innerHTML =
      '<button type="button" class="adg-lb__close" data-lb-close aria-label="Close preview">&times;</button>' +
      '<button type="button" class="adg-lb__nav adg-lb__nav--prev" data-lb-prev aria-label="Previous design"></button>' +
      '<button type="button" class="adg-lb__nav adg-lb__nav--next" data-lb-next aria-label="Next design"></button>' +
      '<div class="adg-lb__inner">' +
        '<div class="adg-lb__stage"><img class="adg-lb__img" alt="" decoding="async"></div>' +
        '<div class="adg-lb__caption">' +
          '<span class="adg-lb__title"></span>' +
          '<span class="adg-lb__artist"></span>' +
          '<span class="adg-lb__legal"></span>' +
          '<a class="adg-lb__cta" data-lb-cta href="#"></a>' +
          '<button type="button" class="adg-lb__cta adg-lb__act" data-lb-act hidden></button>' +
          '<span class="adg-lb__counter"></span>' +
        '</div>' +
      '</div>';
    document.body.appendChild(dlg);

    els = {
      img: dlg.querySelector('.adg-lb__img'),
      title: dlg.querySelector('.adg-lb__title'),
      artist: dlg.querySelector('.adg-lb__artist'),
      cta: dlg.querySelector('[data-lb-cta]'),
      act: dlg.querySelector('[data-lb-act]'),
      counter: dlg.querySelector('.adg-lb__counter'),
      prev: dlg.querySelector('[data-lb-prev]'),
      next: dlg.querySelector('[data-lb-next]'),
      stage: dlg.querySelector('.adg-lb__stage')
    };

    // Clicking the dark surround: ZONES, not a trapdoor. Missing the 44px
    // arrow by a hair used to close the whole viewer (Justin, 08-05) — so
    // the left third of the surround pages back, the right third pages
    // forward, and only the middle actually closes. With one item there is
    // nothing to page and the whole surround closes, as before.
    dlg.addEventListener('click', function (e) {
      if (e.target !== dlg && e.target !== dlg.querySelector('.adg-lb__inner')) return;
      if (items.length > 1) {
        var frac = (e.clientX - dlg.getBoundingClientRect().left) / dlg.getBoundingClientRect().width;
        if (frac < 0.34) { step(-1); return; }
        if (frac > 0.66) { step(1); return; }
      }
      close();
    });
    dlg.querySelector('[data-lb-close]').addEventListener('click', close);
    els.act.addEventListener('click', function (e) {
      e.stopPropagation();
      if (!action || els.act.disabled) return;
      var acted = action.onAct(items[idx], idx);
      if (acted === false) { paintAction(); return; }
      // THE CONFIRMATION BEAT. A tap that only changes a digit is invisible
      // unless the eye is on the digit (Justin, 08-05) — so the button
      // itself announces it: flips to "✓ Added" in the inverted style for a
      // moment, then rests at whatever state() now says ("Add another ·
      // 3 of 8"). Disabled during the beat so a double-tap cannot add two
      // by accident.
      els.act.classList.add('is-confirm');
      els.act.textContent = '✓ Added';
      els.act.disabled = true;
      setTimeout(function () {
        els.act.classList.remove('is-confirm');
        paintAction();
      }, 700);
    });
    els.prev.addEventListener('click', function (e) { e.stopPropagation(); step(-1); });
    els.next.addEventListener('click', function (e) { e.stopPropagation(); step(1); });
    dlg.addEventListener('close', onClosed);

    dlg.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
    });

    // Swipe. Phones are where this viewer earns its keep, and reaching for
    // a 44px arrow with a thumb is worse than flicking the picture.
    var sx = 0, sy = 0, tracking = false;
    els.stage.addEventListener('pointerdown', function (e) {
      tracking = true; sx = e.clientX; sy = e.clientY;
    });
    els.stage.addEventListener('pointerup', function (e) {
      if (!tracking) return;
      tracking = false;
      var dx = e.clientX - sx, dy = e.clientY - sy;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) step(dx < 0 ? 1 : -1);
    });
    els.stage.addEventListener('pointercancel', function () { tracking = false; });

    return dlg;
  }

  /* Two warming ranges, because they cost wildly different amounts.
   *
   * The 32px grounds are 593 bytes each, so a wide net is free: twelve
   * either way is about 14 KB and means every flip inside that window has
   * something to show on the very first frame. That is the whole point —
   * "the nothing part was what we were trying to fill" (Justin, 08-10).
   * You wait for a fade, never for a blank stage.
   *
   * The 1024s are 67.7 KB, so only the immediate neighbours get one; those
   * are the flips that happen fastest and need to be genuinely instant. */
  var FULL_RANGE = 2;
  var GROUND_RANGE = 12;

  function fetchOnce(src) {
    if (!src || preloaded[src]) return;
    var im = new Image();
    im.onload = function () { preloaded[src] = 1; };
    im.src = src;
  }

  function warm(i) {
    var it = items[i];
    if (it) fetchOnce(it.image);
  }

  function warmAround(centre) {
    for (var n = 1; n <= GROUND_RANGE; n++) {
      var a = items[centre + n], b = items[centre - n];
      if (a) fetchOnce(a.lqip);
      if (b) fetchOnce(b.lqip);
      if (n <= FULL_RANGE) { warm(centre + n); warm(centre - n); }
    }
  }

  function paint() {
    var it = items[idx];
    if (!it) return;

    // The <img> is reused between opens, so clear it and fade the new one in
    // once it has actually decoded — otherwise you see the PREVIOUS artwork
    // for a beat, which reads as the viewer showing you the wrong picture.
    var img = els.img;
    img.style.opacity = '0';
    img.removeAttribute('src');
    img.alt = it.title || '';

    // Blur-up. The 32px render (593 bytes) goes behind the stage instantly
    // and is scaled and blurred by CSS, so the shopper sees the shape and
    // colour of the artwork straight away instead of a white rectangle
    // while a 1024 decodes. It is dropped the moment the real image is up.
    var stage = img.parentElement;
    if (it.lqip) {
      stage.style.backgroundImage = 'url("' + it.lqip + '")';
      stage.classList.add('is-blurring');
    } else {
      stage.style.backgroundImage = '';
      stage.classList.remove('is-blurring');
    }
    var settle = function () {
      img.style.opacity = '1';
      stage.classList.remove('is-blurring');
      stage.style.backgroundImage = '';
    };

    img.onload = settle;
    img.src = it.image;
    if (img.complete && img.naturalWidth) settle();

    els.title.textContent = it.title || '';
    els.artist.textContent = it.artist || '';
    els.artist.hidden = !it.artist;
    // Copyright line on every enlarged design (Joel, 2026-08-31); the text
    // is the theme setting, carried on <body data-legal-line>.
    var legal = dlg.querySelector('.adg-lb__legal');
    if (legal) {
      legal.textContent = document.body.dataset.legalLine || '';
      legal.hidden = !legal.textContent;
    }

    if (it.href) {
      els.cta.href = it.href;
      els.cta.textContent = it.ctaLabel || 'View this design';
      els.cta.hidden = false;
    } else {
      els.cta.hidden = true;
    }
    paintAction();

    var many = items.length > 1;
    els.prev.hidden = !many;
    els.next.hidden = !many;
    els.counter.hidden = !many;
    if (many) els.counter.textContent = (idx + 1) + ' of ' + items.length;

    warmAround(idx);
    if (onIndex) onIndex(idx);
  }

  function paintAction() {
    if (!els || !els.act) return;
    if (!action) { els.act.hidden = true; return; }
    var st = action.state(items[idx], idx) || {};
    els.act.textContent = st.label || '';
    els.act.disabled = !!st.disabled;
    els.act.hidden = !st.label;
  }

  function step(delta) {
    if (!items.length) return;
    idx = (idx + delta + items.length) % items.length;
    paint();
  }

  function open(list, start, opts) {
    if (!list || !list.length) return;
    build();
    items = list;
    idx = Math.max(0, Math.min(start || 0, items.length - 1));
    onIndex = (opts && opts.onIndex) || null;
    action = (opts && opts.action) || null;
    paint();

    prevFocus = document.activeElement;
    openedByKeyboard = lastInputKeyboard;
    prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    if (HAS_DIALOG) {
      dlg.showModal();
    } else {
      dlg.setAttribute('open', '');
      dlg.classList.add('adg-lb--fallback');
      escHandler = function (e) { if (e.key === 'Escape') close(); };
      document.addEventListener('keydown', escHandler);
    }
  }

  function close() {
    if (!dlg) return;
    if (HAS_DIALOG && typeof dlg.close === 'function') {
      dlg.close();            // fires 'close' -> onClosed
    } else {
      dlg.removeAttribute('open');
      dlg.classList.remove('adg-lb--fallback');
      onClosed();
    }
  }

  function onClosed() {
    document.body.style.overflow = prevOverflow || '';
    if (escHandler) { document.removeEventListener('keydown', escHandler); escHandler = null; }
    if (els && els.img) { els.img.removeAttribute('src'); els.img.style.opacity = '0'; }
    restoreFocus(prevFocus, openedByKeyboard);
    prevFocus = null;
    onIndex = null;
    action = null;
    if (els && els.act) els.act.hidden = true;
  }

  /* The ⤢ control that opens it. Returned rather than injected so each
     grid can place it in whatever markup it already has. */
  function expandButton(label) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'adg-expand';
    b.setAttribute('aria-label', 'Preview ' + (label || 'this design') + ' larger');
    b.innerHTML = '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">' +
      '<path fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" ' +
      'stroke-linejoin="round" d="M9 2h5v5M14 2L8 8M7 14H2V9M2 14l6-6"/></svg>';
    return b;
  }

  window.ADGLightbox = { open: open, close: close, expandButton: expandButton };
})(window, document);
